// rally 편집기 좌열 — attack 폼
//
// **한 줄을 만드는 자리다.** 타입·맡을 roster·대기 시간·착탄 간격 넷을 받아 시퀀스에
// 적재한다. 적재된 줄을 보여 주는 것은 우열이고, 이 파일은 그쪽을 모른다.
//
// ── 무엇이 일어나는지가 누른 버튼에 달린다 ─────────────────────────────────
//
// 타입을 고르는 것이 버튼 셋인 이유가 이것이다. 드롭다운이면 지금 무엇을 만드는 중인지가
// 접혀 있고, main 이 이미 있는 상태에서 main 을 고를 수 있는지가 열어 봐야 보인다. 명부의
// 등록 폼이 「입력값에 따라 하는 일이 달라지던」 모양을 2026-08-12 에 버린 것과 같은
// 이유다 — 무엇이 일어나는지는 **누른 버튼**이 말해야 한다.
//
// ── main 이 없으면 sub 를 만들 수 없다 ──────────────────────────────────────
//
// main 이 착탄축의 원점이라, 없는 상태에서 sub 를 만들면 그 간격이 무엇 대비인지가
// 정의되지 않는다. 서버도 409 로 막지만 **화면이 먼저 말한다** — 서버만 막으면 CT 는
// 폼을 다 채운 뒤에야 그것을 알게 되고, 그 한 번이 전투 직전이다.
//
// ── 네 옵션을 왜 저 셋으로 보여 주는가 ─────────────────────────────────────
//
// 정본이 「4개 대기 시간 옵션의 결과를 동시에」 요구한다. 손잡이가 가장 이른 개시를
// `지금 + 손잡이 값` 에 붙들고 있어서 「개시 여유」로 보여 주면 새 줄이 가장 먼저 여는
// 경우 네 값이 전부 같아진다 — 계산이 틀려서가 아니라 구조가 그렇다. 실제로 갈리는 것은
// 그 줄의 개시 Δ, 기존 줄이 밀리는 양, 그리고 개시부터 착탄까지의 길이 셋이고, 그 셋을
// 함께 보여 주면 네 옵션이 언제나 서로 다르게 읽힌다. 자세한 유도는 `use-sequence.ts` 의
// `previewOptions` 주석에 있다.
//
// ── 행군을 못 잰 사람은 배정하지 않는다 ────────────────────────────────────
//
// 조용히 0 으로 두면 그 rally 만 지금 당장 열리는 것으로 계산되고, 그 값은 화면에서
// 정상으로 보인다. 그래서 목록에 남기되 **왜 못 고르는지와 어디서 고치는지**를 함께
// 말한다 — 목록에서 지우면 CT 는 그 사람이 명부에 없다고 읽는다.

import { useCallback, useMemo, useState } from 'react'

import type { Roster, Sequence } from '../../../../contract/schema.ts'
import { duration } from '../../../../core/time/brand.ts'
import type { RallyKind } from '../../../../core/timing/attack.ts'
// **착탄 간격의 형식기는 타이밍 코어의 것 하나다.** 미니 시퀀스에도 같은 일을 하는 조각이
// 있지만 그쪽에서 가져오면 두 파일이 서로를 부르는 고리가 생기고, 그 고리에서는 상수가
// 어느 쪽이 먼저 평가되느냐에 따라 `undefined` 가 된다 — 타입 검사는 그것을 보지 못한다.
import { formatSignedOffset } from '../../../../core/timing/format.ts'
import {
  type CommonStrings,
  common as commonStrings,
  type ComposeStrings,
  compose as composeStrings,
} from '../../../../i18n/index.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { kindLabel, kindMark } from '../../../ui/rally-kind.ts'
import { Icon, IconTile } from '../../../ui/icons.tsx'
import { LabelHint } from '../../../ui/label-hint.tsx'
import { formatTimeText, parseTimeText, TimeInput, timeParseMessage } from '../../../ui/time-input.tsx'
import {
  type LiveContext,
  previewOptions,
  type RallyingOption,
  type RallyInput,
} from '../attack/use-sequence.ts'

/** 지금 고른 언어의 문자열 한 벌. **모듈에 굳히지 않는다** — 굳히면 언어를 바꿔도 안 따라온다. */
function useStrings(): ComposeStrings {
  return composeStrings(useLang().lang)
}

/** rally 타입 이름과 표식은 열람 카드도 같은 글자를 내야 해서 공용 네임스페이스가 진다. */
function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

/** 문장 안의 `{자리}` 를 채운다. 번역이 붙어 어순이 달라져도 견디게 하는 최소한이다. */
function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  )
}

const KINDS: readonly RallyKind[] = ['main', 'strike', 'garrison']

/**
 * 타입별 테두리 색. **색 단독으로 뜻을 지지 않는다** — 표식 문자를 늘 함께 적는다.
 *
 * **글자는 여기 없다** (2026-08-17). 표식 문자 표가 이 파일과 열람 카드에 똑같이 두 벌
 * 적혀 있었고, 같아야 하는 것을 두 벌로 적으면 갈린다. 지금은 공용 카탈로그가 지고
 * `src/web/ui/rally-kind.ts` 가 꺼내 준다. 색이 여기 남는 것은 두 화면의 시각 처리가
 * 애초에 다르기 때문이다 — 이쪽은 테두리, 저쪽은 글자색이다.
 */
export const KIND_BORDER: Record<RallyKind, string> = {
  main: 'border-[#E8C35C]',
  strike: 'border-[#FF5C8A]',
  garrison: 'border-[#5CCB8F]',
}

/**
 * 대기 시간 네 옵션. **두 문맥이 같은 목록을 쓴다.**
 *
 * 조립 폼과 내보낸 뒤의 줄 수정 패널이 각자 이 목록을 그리면 「불가 옵션은 눌리지 않는다」가
 * 두 벌이 되고, 한쪽만 고치는 날이 온다. 미니 시퀀스와 attack 카드가 배치를 따로 적던 동안
 * 좁은 폭에서 서로 다르게 무너진 것과 같은 종류의 실패다.
 *
 * **불가 옵션은 `disabled` 로 둔다.** 눌리는데 거절하는 것과 안 눌리는 것은 다르고, 판정
 * 계약이 이 항목에 「그 옵션으로는 confirm 이 되지 않는다」를 이름으로 적었다.
 */
export function RallyingOptionList(props: {
  options: readonly RallyingOption[]
  selected: number
  onSelect: (rallyingMs: number) => void
  testId?: string
  /**
   * 불가 옵션 옆에 「간격이 얼마 이상이어야 붙는지」를 적을 것인가. 기본은 적는다.
   *
   * **main 을 고칠 때는 끈다.** main 은 착탄축의 원점이라 그 자신의 간격이 정의상 0 이고
   * CT 가 그 값을 바꿀 수 없다. 그 자리에 「간격을 이만큼으로 하세요」가 뜨면 화면이 할 수
   * 없는 일을 시키는 것이고, 그것은 아무 말도 안 하는 것보다 나쁘다. main 에서 실제로
   * 움직일 수 있는 손잡이는 대기 시간과 담당자이고, 그 둘은 이 목록과 아래 select 가
   * 이미 보여 준다.
   */
  showMinimum?: boolean
}): React.ReactElement {
  const t = useStrings()
  const showMinimum = props.showMinimum ?? true
  return (
    <ul className="mt-line flex flex-col gap-1" data-testid={props.testId ?? 'rallying-options'}>
      {props.options.map((option) => (
        <li key={option.rallyingMs}>
          <button
            type="button"
            data-testid={`rallying-option-${String(option.minutes)}`}
            data-rallying-ms={String(option.rallyingMs)}
            data-feasible={String(option.feasible)}
            data-active={String(option.rallyingMs === props.selected)}
            disabled={!option.feasible}
            className={`seg grid w-full grid-cols-[2.75rem_minmax(0,1fr)] items-center justify-items-start gap-x-3 py-2 text-left ${
              option.rallyingMs === props.selected ? 'is-on' : ''
            }`}
            onClick={() => {
              props.onSelect(option.rallyingMs)
            }}
          >
            <span className="text-base font-semibold text-ink">
              {fill(t.rallyingMinutes, { n: String(option.minutes) })}
            </span>
            {/* 장식 묶음(감싸는 span): 값들을 이름 칸 오른쪽 한 열에 모은다. 순서와 글자는 그대로다. */}
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            {/* 개시부터 착탄까지. **네 옵션을 언제나 가르는 값이 이것이다.** */}
            <span className="font-mono tabular-nums text-ink-dim">
              {t.columnOpenDelta} → {t.columnImpactDelta} {formatTimeText(option.openToImpactMs)}
            </span>
            {option.leadMs === null ? (
              <>
                <span className="text-ink-dim">
                  {option.openDeltaMs === 0
                    ? t.rallyingLeadFirst
                    : fill(t.rallyingLeadAfter, { value: formatTimeText(option.openDeltaMs) })}
                </span>
                <span className="basis-full text-xs text-ink-mute">
                  {option.shiftMs === 0
                    ? t.rallyingShiftNone
                    : fill(t.rallyingShift, { value: formatTimeText(option.shiftMs) })}
                </span>
              </>
            ) : (
              // **진행 중 문맥에서는 개시 여유가 옵션을 가른다.** 원점이 얼어붙어 앞 줄이
              // 밀리지 않으므로 「가장 이른 개시 대비 Δ」와 「밀리는 양」은 둘 다 구조적으로
              // 0 이고, 0 넷을 나란히 그리면 화면이 네 값을 다르게 말하는 척하게 된다.
              <span
                data-testid={`rallying-lead-${String(option.minutes)}`}
                className="font-mono tabular-nums text-ink-dim"
              >
                {fill(t.rallyingLead, { value: formatTimeText(option.leadMs) })}
              </span>
            )}
            {/* **불가한 줄을 흐리게 만들지 않는다** (2026-08-19). 전에는 이 버튼 전체가
                `opacity-40` 이었고, 그래서 바로 아래 「얼마면 되는가」 문장이 2.38:1 로
                떨어졌다 — CT 가 간격을 손으로 더듬지 않게 하려고 넣은 문장이 정확히 그
                더듬음이 필요한 순간에 안 읽혔다. 못 누른다는 사실은 `disabled` 와 아래
                붉은 「불가」가 이미 말한다. */}
            {!option.feasible && <span className="text-red-300">{t.rallyingBlocked}</span>}
            {/* **불가일 때 「얼마면 되는가」를 함께 준다.** 없으면 CT 가 간격을 손으로
                더듬게 되고, 그 더듬는 일이 이 앱이 없애려는 수기 작업 그 자체다. */}
            {showMinimum && !option.feasible && option.minimumOffsetMs !== null && (
              <span
                data-testid={`rallying-minimum-${String(option.minutes)}`}
                className="font-mono tabular-nums text-accent"
              >
                {fill(t.rallyingMinimumOffset, {
                  value: formatSignedOffset(duration(option.minimumOffsetMs)),
                })}
              </span>
            )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export interface RallyFormAttackProps {
  allies: Roster[]
  sequence: Sequence
  onAdd: (input: RallyInput) => Promise<string | null>
  /**
   * 진행 중 시퀀스면 온다 — **counter rally 문맥이다.**
   *
   * 조립 문맥과 갈리는 것이 셋이다. 착탄 원점이 얼어붙어 새 줄이 앞 줄을 밀지 못하고,
   * 하한을 어기는 조합이 실제로 만들어지며, main 은 이미 있어 고를 수 없다.
   */
  live?: LiveContext | null
}

export function RallyFormAttack(props: RallyFormAttackProps): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const [kind, setKind] = useState<RallyKind>('main')
  const [rosterId, setRosterId] = useState('')
  const [rallyingMs, setRallyingMs] = useState(300_000)
  const [offsetText, setOffsetText] = useState('')
  const [message, setMessage] = useState('')
  /** 방금 붙였다는 말. **counter rally 문맥에만 산다.** 조립 문맥은 미니 시퀀스가 맡는다. */
  const [notice, setNotice] = useState('')

  const hasMain = props.sequence.rallies.some((line) => line.type === 'main')
  // main 이 이미 있으면 처음부터 sub 를 고르고 있게 한다. 고를 수 없는 것을 골라 둔 채로
  // 열면 첫 클릭이 언제나 「타입 바꾸기」가 된다.
  const effectiveKind: RallyKind = kind === 'main' && hasMain ? 'strike' : kind
  const isMain = effectiveKind === 'main'

  const picked = props.allies.find((row) => row.id === rosterId) ?? null
  const marchingMs = picked?.marchingMs ?? null

  const offsetParsed = parseTimeText(offsetText)
  const offsetMs = isMain ? 0 : offsetParsed.ok ? offsetParsed.millis : 0

  const live = props.live ?? null
  const options = useMemo(
    () =>
      previewOptions({
        existing: props.sequence.rallies,
        offsetMs,
        marchingMs: marchingMs ?? 0,
        handleMs: props.sequence.handleMs,
        live,
      }),
    [live, marchingMs, offsetMs, props.sequence.handleMs, props.sequence.rallies],
  )

  const chosen = options.find((option) => option.rallyingMs === rallyingMs) ?? null

  /**
   * 값이 바뀌면 그 값에 대해 하던 말을 거둔다. 명부가 세운 규칙을 그대로 빌린다.
   *
   * **붙였다는 말도 함께 거둔다.** 그 문장은 방금 붙인 줄에 대한 것이라, 다음 줄을 만들기
   * 시작한 화면에 남아 있으면 지금 만드는 줄이 이미 붙은 것처럼 읽힌다.
   */
  const clearSaid = useCallback((): void => {
    setMessage('')
    setNotice('')
  }, [])

  const submit = useCallback(async (): Promise<void> => {
    if (!isMain && !hasMain) {
      setMessage(t.needMainFirst)
      return
    }
    if (picked === null) {
      // **거절은 select 자리표시와 다른 글자를 쓴다.** 둘이 같은 「고르세요」였을 때는
      // 무엇을 고르라는 것인지 문장이 말하지 않았고, 이 화면의 다른 거절은 전부 한
      // 문장이다. (2026-08-13 대리 관측의 잔소리 넷 중 하나.)
      setMessage(t.rosterNeed)
      return
    }
    if (marchingMs === null) {
      setMessage(fill(t.rosterNeedsMarchingWhy, { name: picked.nickname }))
      return
    }
    if (!isMain && !offsetParsed.ok) {
      setMessage(timeParseMessage(offsetParsed.reason, c))
      return
    }
    // **불가 옵션으로는 적재되지 않는다.** 지금 구조에서 이 갈래가 안 도는 것이 정상이다 —
    // 편집 문맥에서는 손잡이가 가장 이른 개시를 붙들고 있어 하한을 어길 수 없고, 어길 수
    // 있는 것은 절대 시각이 얼어붙은 진행 중 시퀀스뿐이라 그쪽 문은 재구성 스텝이 연다.
    if (chosen !== null && !chosen.feasible) {
      setMessage(t.rallyingBlocked)
      return
    }

    // 보낼 때의 값을 붙들어 둔다. 아래 초기화가 이 값과 견준다.
    const sentRosterId = picked.id
    const sentOffset = offsetText
    const failed = await props.onAdd({
      type: effectiveKind,
      rosterId: picked.id,
      rallyingMs,
      offsetMs,
    })
    if (failed !== null) {
      setMessage(failed)
      return
    }
    // **붙었으면 붙었다고 말한다.** 조립 문맥에서는 우열의 미니 시퀀스가 「방금 적재」로
    // 그 일을 하는데, counter rally 문맥에는 그 패널이 없던 시절 확인을 맡던 장치가 사라진
    // 채 대체물이 서지 않았다. 폼이 그냥 비워지고, 붙은 것을 그 화면에서 알 방법이 없었다.
    // 2026-08-27 부터 목록이 이 문맥에도 서지만 폼 안의 확인 문장은 남긴다(전투 중 시선이
    // 폼에 있다). 두 장치가 겹치는 것을 의도로 둘지 하나로 줄일지는 사용자 판단이라 여기서
    // 정하지 않는다.
    // 이 제품의 다른 조작은 전부 확인 문장을 주는데 이 한 자리에서만 관례가 깨졌고, 하필
    // 여기가 그 관례가 가장 필요한 자리다. counter rally 를 붙이는 순간은 정의상 전투가
    // 진행 중이라 CT 에게 탭을 옮겨 확인할 여유가 없다.
    // (2026-08-15 대리 관측이 D2 에서 1점으로 값 매겼다.)
    //
    // **「앞 줄은 그대로입니다」를 함께 말한다.** 진행 중 시퀀스에 줄을 더하는 CT 가 가장
    // 불안해하는 것이 그것이고, 폼 안에는 그 말이 예고로만 있고 결과로는 없었다.
    setNotice(
      live === null
        ? ''
        : fill(t.counterAdded, {
            name: picked.nickname,
            offset: formatSignedOffset(duration(offsetMs)),
          }),
    )
    setMessage('')
    // **초기화는 보낼 때의 값이 아직 칸에 있을 때만 한다.** 우열 목록은 방송(`sequence.updated`)
    // 으로 먼저 늘고 이 응답은 그 뒤에 오는데, CT 가 그 사이에 다음 줄의 roster 를 골라 두면
    // 늦게 도착한 이 초기화가 그 선택을 지우고 다음 적재가 「누가 맡을지 고르세요」로 거절된다.
    // 2026-08-27 게이트가 부하 속에서 그 경합을 실제로 밟았다(둘째 줄이 안 들어감). 명부와
    // time handle 칸이 같은 경합에 같은 처방을 쓴다.
    setOffsetText((current) => (current === sentOffset ? '' : current))
    setRosterId((current) => (current === sentRosterId ? '' : current))
  }, [
    chosen,
    effectiveKind,
    hasMain,
    isMain,
    marchingMs,
    offsetMs,
    offsetParsed,
    picked,
    props,
    rallyingMs,
    t,
  ])

  return (
    <section
      data-testid="rally-form-attack"
      data-mode={live === null ? 'compose' : 'counter'}
      aria-label={live === null ? t.formTitle : t.counterTitle}
      className="card min-w-0"
    >
      <div className="panel-head cat-accent">
        <IconTile name="plus" tone="accent" />
        <h3 className="section-title">{live === null ? t.formTitle : t.counterTitle}</h3>
      </div>
      {/* **왜 진행 중인데 줄을 더하는가.** 우리가 점령하는 순간 그 건물에 rally 를 열 수
          없게 되는데 상대는 즉시 반격해 온다. 이 문장이 없으면 「이미 나간 작전에 왜 줄을
          더하지」가 화면 어디에도 답이 없다. */}
      {live !== null && <p className="mt-line text-xs text-ink-mute">{t.counterWhy}</p>}

      {/* ── 타입 ────────────────────────────────────────────────────────── */}
      <fieldset className="mt-group min-w-0">
        <legend className="text-xs font-medium text-ink-dim">{t.typeLabel}</legend>
        <div className="mt-line flex flex-wrap gap-1.5">
          {KINDS.map((candidate) => {
            const locked = candidate === 'main' ? hasMain : !hasMain
            const active = candidate === effectiveKind
            return (
              <button
                key={candidate}
                type="button"
                data-testid={`rally-type-${candidate}`}
                data-active={String(active)}
                disabled={locked}
                aria-label={kindLabel(candidate, c)}
                // 못 고르는 칸은 타입 색 테두리를 걷고 중립 점선(분절 칸의 비활성 규칙)만 남긴다. 타입 색이 남으면 그 점선이 시퀀스의
                // 「방금 적재」 줄(타입 색 왼쪽 점선)과 같은 꼴로 읽혔다(2026-10-13 비평 둘이 겹친 지적).
                className={`seg seg-kind ${KIND_BORDER[candidate]} disabled:border-transparent ${active ? 'is-on' : ''}`}
                onClick={() => {
                  setKind(candidate)
                  clearSaid()
                }}
              >
                <span className={`kind-tile kind-${candidate} font-mono`}>{kindMark(candidate, c)}</span>{' '}
                {kindLabel(candidate, c)}
              </button>
            )
          })}
        </div>
        {/* 잠긴 이유를 화면이 말한다. 서버만 막으면 폼을 다 채운 뒤에 알게 된다.
            진행 중 문맥에서는 잠긴 이유가 다르다 — main 이 이미 **확정된 원점**이라
            둘째를 만들 수 없는 것이지, 자리가 찼을 뿐인 것이 아니다. */}
        <p className="mt-line text-xs text-ink-mute">
          {live !== null ? t.counterMainFixed : hasMain ? t.typeMainTaken : t.needMainFirst}
        </p>
      </fieldset>

      {/* ── 맡을 roster ─────────────────────────────────────────────────── */}
      <label className="mt-group flex min-w-0 flex-col gap-1 text-xs">
        <span className="font-medium text-ink-dim">{t.rosterLabel}</span>
        <select
          data-testid="rally-roster"
          className="field px-2.5 text-sm"
          value={rosterId}
          onChange={(event) => {
            setRosterId(event.target.value)
            clearSaid()
          }}
        >
          <option value="">{props.allies.length === 0 ? t.rosterEmpty : t.rosterPick}</option>
          {props.allies.map((row) => (
            <option key={row.id} value={row.id}>
              {/* 행군을 못 잰 사람도 목록에 남긴다 — 지우면 명부에 없는 것으로 읽힌다. */}
              {row.marchingMs === null
                ? `${row.nickname} (${t.rosterNeedsMarching})`
                : row.nickname}
            </option>
          ))}
        </select>
      </label>
      {picked !== null && marchingMs === null && (
        <p data-testid="rally-roster-needs-marching" className="mt-line text-xs text-accent">
          {fill(t.rosterNeedsMarchingWhy, { name: picked.nickname })}
        </p>
      )}

      {/* ── 착탄 간격 — 라벨이 정본 원문으로 고정돼 있다 ─────────────────── */}
      {isMain ? (
        <p className="mt-group text-xs text-ink-mute">{t.offsetMainZero}</p>
      ) : (
        <div className="mt-group">
          <TimeInput
            testId="rally-offset"
            // **원문 고정이다.** 착탄축이라는 사실을 말로 설명하는 대신 수식으로 못박은
            // 형태이고, 이 값을 개시축으로 오해하는 순간 CT 는 이 제품이 없애려는 실패를
            // 스스로 만든다 — 같은 −1.0초가 착탄축에서는 1초 먼저, 개시축에서는 59초 늦다.
            label={t.offsetLabel}
            value={offsetText}
            message={
              offsetText !== '' && !offsetParsed.ok ? timeParseMessage(offsetParsed.reason, c) : ''
            }
            onChange={(next) => {
              setOffsetText(next)
              clearSaid()
            }}
          />
        </div>
      )}

      {/* ── 대기 시간 네 옵션 — 결과를 동시에 본다 ───────────────────────── */}
      <fieldset className="mt-group min-w-0">
        {/* 범례는 라벨(rallying)과 뜻풀이 두 요소다. 둘 다 12px 이라 굵기와 색, 간격으로 가른다(2026-10-14 사용자 지시). */}
        <legend className="text-xs font-medium text-ink-dim">
          <LabelHint label={t.rallyingLabel} hint={t.rallyingLabelHint} hintClassName="font-normal text-ink-mute" />
        </legend>
        <p className="mt-line text-xs text-ink-mute">{live === null ? t.rallyingWhy : t.rallyingNoShift}</p>
        <RallyingOptionList
          options={options}
          selected={rallyingMs}
          onSelect={(next) => {
            setRallyingMs(next)
            clearSaid()
          }}
        />
      </fieldset>

      <button
        type="button"
        data-testid="rally-confirm"
        className={`btn mt-group w-full ${
          live === null && props.sequence.state !== 'confirmed' && !hasMain ? 'btn-primary' : 'btn-secondary'
        }`}
        onClick={() => void submit()}
      >
        <Icon name="plus" />
        {t.confirmRally}
      </button>

      {notice !== '' && (
        <p data-testid="rally-form-notice" role="status" className="mt-group text-sm text-cyan">
          {notice}
        </p>
      )}
      {message !== '' && (
        <p data-testid="rally-form-message" role="alert" className="mt-group text-sm text-red-300">
          {message}
        </p>
      )}
    </section>
  )
}
